import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { api } from '../services/api';
import {
  FileText, Image as ImageIcon, FileType, Download, Trash2, Search,
  Upload, ShieldCheck, AlertCircle, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { getCategoryColor } from '../utils/helpers';

const TYPE_LABELS = {
  insurance:   'Insurance',
  certificate: 'Certificate',
  receipt:     'Receipt',
  warranty:    'Warranty',
  valuation:   'Valuation',
  provenance:  'Provenance',
  other:       'Other',
};

const TYPE_TONES = {
  insurance:   'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  certificate: 'bg-[#627EEA]/15 text-[#627EEA] border-[#627EEA]/25',
  receipt:     'bg-gold/15 text-gold border-gold/25',
  warranty:    'bg-violet-500/15 text-violet-400 border-violet-500/25',
  valuation:   'bg-amber-500/15 text-amber-400 border-amber-500/25',
  provenance:  'bg-cyan-500/15 text-cyan-400 border-cyan-500/25',
  other:       'bg-zinc-500/15 text-zinc-400 border-zinc-500/25',
};

const formatBytes = (bytes) => {
  if (!bytes) return ' - ';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const fileIcon = (doc) => {
  const ct = (doc.content_type || '').toLowerCase();
  if (ct.startsWith('image/')) return ImageIcon;
  if (ct === 'application/pdf') return FileType;
  return FileText;
};

const downloadFile = async (doc) => {
  try {
    const token = localStorage.getItem('token');
    const url = api.getDocumentDownloadUrl(doc.id);
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = doc.original_filename || `${doc.name}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
  } catch (e) {
    toast.error('Could not download file');
  }
};

const previewFile = async (doc) => {
  try {
    const token = localStorage.getItem('token');
    const url = api.getDocumentDownloadUrl(doc.id);
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error('Preview failed');
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (e) {
    toast.error('Could not open file');
  }
};

export default function Vault() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [assetFilter, setAssetFilter] = useState('all');

  const fetchVault = async () => {
    try {
      const r = await api.getVault();
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVault();
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.documents.filter(d => {
      if (typeFilter !== 'all' && (d.document_type || 'other') !== typeFilter) return false;
      if (assetFilter !== 'all' && d.asset_id !== assetFilter) return false;
      if (!q) return true;
      return (
        (d.name || '').toLowerCase().includes(q) ||
        (d.notes || '').toLowerCase().includes(q) ||
        (d.original_filename || '').toLowerCase().includes(q) ||
        (d.asset_name || '').toLowerCase().includes(q)
      );
    });
  }, [data, search, typeFilter, assetFilter]);

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"? This cannot be undone.`)) return;
    try {
      await api.deleteDocument(doc.id);
      toast.success('Document deleted');
      fetchVault();
    } catch {
      toast.error('Could not delete');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="vault-loading">
          <Skeleton className="h-3 w-24 mb-3 bg-gold/10" />
          <Skeleton className="h-9 w-72 mb-8 bg-white/5" />
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[0,1,2,3].map(i => <Skeleton key={i} className="h-24 bg-white/5" />)}
          </div>
          {[0,1,2].map(i => <Skeleton key={i} className="h-20 bg-white/5 mb-3" />)}
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="vault-error">
          <p className="text-muted-foreground">Could not load the vault. Please refresh.</p>
        </div>
      </DashboardLayout>
    );
  }

  const s = data.summary || {};
  const typeFilters = [
    { id: 'all', label: 'All', count: s.total_count || 0 },
    ...Object.keys(TYPE_LABELS)
      .map(k => ({ id: k, label: TYPE_LABELS[k], count: s.counts_by_type?.[k] || 0 }))
      .filter(f => f.count > 0),
  ];

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="vault-page">
        <div className="mb-8 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <p className="text-gold uppercase tracking-[0.2em] text-xs mb-2">The Vault</p>
            <h1 className="font-serif text-3xl text-white">Every document, in one secure place</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
              Insurance schedules, valuations, certificates of authenticity and receipts - encrypted at rest,
              accessible only to you. We never share your files with third parties.
            </p>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="vault-summary">
          <div className="luxury-card p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Documents</p>
            <p className="font-serif text-2xl text-white">{s.total_count || 0}</p>
          </div>
          <div className="luxury-card p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">With files</p>
            <p className="font-serif text-2xl text-gold">{s.with_file_count || 0}</p>
          </div>
          <div className="luxury-card p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Storage used</p>
            <p className="font-serif text-2xl text-white">{formatBytes(s.total_bytes)}</p>
          </div>
          <div className="luxury-card p-4 border-emerald-500/20">
            <p className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1 flex items-center gap-1">
              <ShieldCheck size={11} /> Encrypted
            </p>
            <p className="text-xs text-white/80">All files are encrypted at rest</p>
          </div>
        </div>

        {/* Search + filter row */}
        <div className="luxury-card p-4 mb-6" data-testid="vault-filters">
          <div className="flex items-center gap-3 mb-3">
            <Search size={14} className="text-muted-foreground flex-shrink-0" />
            <input
              type="text"
              placeholder="Search by name, asset, or filename..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="vault-search"
              className="flex-1 bg-transparent text-sm text-white placeholder:text-muted-foreground/60 focus:outline-none"
            />
          </div>

          {/* Type chips */}
          <div className="flex items-center gap-2 flex-wrap">
            {typeFilters.map(f => (
              <button
                key={f.id}
                onClick={() => setTypeFilter(f.id)}
                data-testid={`vault-type-${f.id}`}
                className={`px-3 py-1 text-[11px] uppercase tracking-wider rounded border transition-colors ${
                  typeFilter === f.id
                    ? 'bg-gold/15 text-gold border-gold/40'
                    : 'bg-transparent text-muted-foreground border-[#262626] hover:border-white/20 hover:text-white'
                }`}
              >
                {f.label} <span className="opacity-60 ml-1">{f.count}</span>
              </button>
            ))}
          </div>

          {/* Asset filter */}
          {data.assets.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap mt-2 pt-2 border-t border-[#1A1A1A]">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Asset:</span>
              <button
                onClick={() => setAssetFilter('all')}
                data-testid="vault-asset-all"
                className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${
                  assetFilter === 'all'
                    ? 'bg-white/10 text-white border-white/30'
                    : 'bg-transparent text-muted-foreground border-[#262626] hover:border-white/20'
                }`}
              >All</button>
              {data.assets.map(a => (
                <button
                  key={a.id}
                  onClick={() => setAssetFilter(a.id)}
                  data-testid={`vault-asset-${a.id}`}
                  className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${
                    assetFilter === a.id
                      ? 'bg-white/10 text-white border-white/30'
                      : 'bg-transparent text-muted-foreground border-[#262626] hover:border-white/20'
                  }`}
                >{a.name}</button>
              ))}
            </div>
          )}
        </div>

        {/* Document list */}
        {filtered.length === 0 ? (
          <div className="luxury-card p-10 text-center" data-testid="vault-empty">
            <Upload size={32} className="text-muted-foreground mx-auto mb-3" />
            <h3 className="font-serif text-xl text-white mb-2">
              {data.documents.length === 0 ? 'Your vault is empty' : 'No documents match these filters'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
              {data.documents.length === 0
                ? 'Upload insurance schedules, valuations and certificates from any asset page - they\'ll all appear here.'
                : 'Try clearing the search or filters.'}
            </p>
            {data.documents.length === 0 && (
              <Link to="/assets">
                <Button className="btn-primary" data-testid="vault-go-to-assets">Go to Assets</Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3" data-testid="vault-list">
            {filtered.map(doc => {
              const Icon = fileIcon(doc);
              const tone = TYPE_TONES[doc.document_type] || TYPE_TONES.other;
              return (
                <div
                  key={doc.id}
                  className="luxury-card p-4 flex items-center gap-4 hover:border-gold/30 transition-colors group"
                  data-testid={`vault-doc-${doc.id}`}
                >
                  <div className={`w-10 h-10 rounded flex items-center justify-center ${tone}`}>
                    <Icon size={18} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="text-white text-sm font-medium truncate">{doc.name}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${tone}`}>
                        {TYPE_LABELS[doc.document_type] || 'Other'}
                      </span>
                      {!doc.has_file && doc.file_url && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-500/15 text-zinc-400 border border-zinc-500/25">
                          External link
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                      <Link to={`/assets/${doc.asset_id}`} className={`${getCategoryColor(doc.asset_category)} hover:underline`} data-testid={`vault-doc-asset-${doc.id}`}>
                        {doc.asset_name}
                      </Link>
                      <span className="text-zinc-600">·</span>
                      <span>{formatDate(doc.created_at)}</span>
                      {doc.has_file && (
                        <>
                          <span className="text-zinc-600">·</span>
                          <span>{formatBytes(doc.size_bytes)}</span>
                        </>
                      )}
                    </p>
                    {doc.notes && (
                      <p className="text-[11px] text-muted-foreground/70 mt-1 truncate">{doc.notes}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    {doc.has_file ? (
                      <>
                        <button
                          onClick={() => previewFile(doc)}
                          className="p-2 text-muted-foreground hover:text-gold transition-colors"
                          title="Open"
                          data-testid={`vault-doc-preview-${doc.id}`}
                        >
                          <ChevronRight size={16} />
                        </button>
                        <button
                          onClick={() => downloadFile(doc)}
                          className="p-2 text-muted-foreground hover:text-gold transition-colors"
                          title="Download"
                          data-testid={`vault-doc-download-${doc.id}`}
                        >
                          <Download size={16} />
                        </button>
                      </>
                    ) : doc.file_url ? (
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-muted-foreground hover:text-gold transition-colors"
                        title="Open external link"
                        data-testid={`vault-doc-link-${doc.id}`}
                      >
                        <ChevronRight size={16} />
                      </a>
                    ) : null}
                    <button
                      onClick={() => handleDelete(doc)}
                      className="p-2 text-muted-foreground hover:text-red-400 transition-colors"
                      title="Delete"
                      data-testid={`vault-doc-delete-${doc.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer hint */}
        <div className="mt-8 luxury-card p-4 flex items-center gap-3 border-amber-500/20" data-testid="vault-hint">
          <AlertCircle size={16} className="text-amber-400 flex-shrink-0" />
          <p className="text-xs text-muted-foreground flex-1">
            Files are uploaded to encrypted object storage with a 10 MB per-file limit. Permitted types: PDF, JPG, PNG, GIF, WEBP, TXT, CSV.
            To upload a new document, open any asset and use the "Add" button in its Documents section.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}
